import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Plus, Trash2 } from 'lucide-react';
import { getYearLabelsForTable, replaceYearPlaceholders } from '../../../utils/reportingYearUtils';
import { normalizeFYResponse } from '../utils/fyNormalization';

/**
 * Helper to get FY/CY labels based on reporting year and organization settings
 * Supports both "FY 2025-2026" and "CY 2025" formats
 * @param {Object} allResponses - Contains reporting_year and optionally year_type/framework
 * @returns {{ current: string, previous: string, yearType: string }}
 */
const getFYLabels = (allResponses) => {
  const reportingYear = allResponses?.reporting_year;
  const yearType = allResponses?.year_type || allResponses?.reporting_year_type || 'financial_year';
  const framework = allResponses?.framework || null;
  
  return getYearLabelsForTable({ reportingYear, yearType, framework });
};

export function DynamicTableRenderer({ config, value, onChange, isEditing }) {
  const tableConfig = config.table_config || {};
  const columns = tableConfig.columns || [];
  const rows = Array.isArray(value) ? value : [{}];

  const handleCellChange = (rowIdx, colKey, cellValue) => {
    const newRows = [...rows];
    if (!newRows[rowIdx]) newRows[rowIdx] = {};
    newRows[rowIdx][colKey] = cellValue;
    onChange(newRows);
  };

  const addRow = () => onChange([...rows, {}]);
  const removeRow = (index) => {
    if (rows.length > 1) onChange(rows.filter((_, i) => i !== index));
  };

  if (!isEditing) {
    return (
      <div className="mt-2 overflow-x-auto">
        <Table className="table-fixed [&_th]:whitespace-normal [&_th]:break-words [&_td]:break-words">
          <TableHeader>
            <TableRow className="bg-stone-50">
              {columns.map((col) => (
                <TableHead key={col.key} className="text-xs font-medium">{col.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell key={col.key} className="text-xs">{row[col.key] || '-'}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-emerald-50">
              {columns.map((col) => (
                <TableHead key={col.key} className="text-xs font-medium" style={{ width: col.width }}>{col.label}</TableHead>
              ))}
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {columns.map((col) => (
                  <TableCell key={col.key} className="p-1">
                    {col.type === 'auto_increment' ? (
                      <span className="text-xs font-medium">{rowIdx + 1}</span>
                    ) : col.type === 'yes_no' ? (
                      <Select value={row[col.key] || ''} onValueChange={(v) => handleCellChange(rowIdx, col.key, v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Y</SelectItem>
                          <SelectItem value="no">N</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : col.type === 'select' ? (
                      <Select value={row[col.key] || ''} onValueChange={(v) => handleCellChange(rowIdx, col.key, v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {(col.options || []).map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : col.type === 'date' ? (
                      <Input type="date" value={row[col.key] || ''} onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)} className="h-8 text-xs" />
                    ) : col.type === 'url' ? (
                      <Input type="url" value={row[col.key] || ''} onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)} className="h-8 text-xs" placeholder="https://..." />
                    ) : col.type === 'expandable_text' || col.type === 'textarea' ? (
                      <Textarea value={row[col.key] || ''} onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)} className="text-xs min-h-[60px]" placeholder={col.label} />
                    ) : col.type === 'number' ? (
                      <Input type="number" value={row[col.key] || ''} onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)} className="h-8 text-xs" step="0.01" />
                    ) : (
                      <Input value={row[col.key] || ''} onChange={(e) => handleCellChange(rowIdx, col.key, e.target.value)} className="h-8 text-xs" placeholder={col.label} />
                    )}
                  </TableCell>
                ))}
                <TableCell className="p-1">
                  <Button variant="ghost" size="sm" onClick={() => removeRow(rowIdx)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {tableConfig.allow_add_row !== false && (
        <Button variant="outline" size="sm" onClick={addRow}>
          <Plus className="w-3 h-3 mr-1" /> Add Row
        </Button>
      )}
    </div>
  );
}

export function NGRBCReviewMatrixRenderer({ config, value, onChange, isEditing }) {
  const tableConfig = config.table_config || {};
  const rows = tableConfig.fixed_rows || [];
  const principles = tableConfig.principles || Array.from({ length: 9 }, (_, index) => `P${index + 1}`);
  const reviewOptions = tableConfig.review_options || ['Director', 'Committee of the Board', 'Any Other Committee'];
  const frequencyOptions = tableConfig.frequency_options || ['Annually', 'Half Yearly', 'Quarterly', 'Any Other'];
  const data = value?.rows || {};

  const updateCell = (rowKey, groupKey, principle, nextValue) => {
    onChange({
      ...(value || {}),
      rows: {
        ...data,
        [rowKey]: {
          ...(data[rowKey] || {}),
          [groupKey]: {
            ...(data[rowKey]?.[groupKey] || {}),
            [principle]: nextValue,
          },
        },
      },
    });
  };

  const renderCell = (row, groupKey, principle, options) => {
    const cellValue = data[row.key]?.[groupKey]?.[principle] || '';
    if (!isEditing) return <span className="text-xs">{cellValue || '-'}</span>;
    return (
      <Select value={cellValue} onValueChange={(nextValue) => updateCell(row.key, groupKey, principle, nextValue)}>
        <SelectTrigger className="h-8 min-w-28 text-xs" data-testid={`brsr-review-${row.key}-${groupKey}-${principle}`}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="mt-3 overflow-x-auto border border-stone-200" data-testid="brsr-ngrbc-review-matrix">
      <Table className="min-w-[2200px]">
        <TableHeader>
          <TableRow className="bg-stone-100">
            <TableHead rowSpan={2} className="sticky left-0 z-20 w-72 border-r bg-stone-100 text-xs">Subject for Review</TableHead>
            <TableHead colSpan={principles.length} className="border-r text-center text-xs">
              Indicate whether review was undertaken by Director / Committee of the Board / Any other Committee
            </TableHead>
            <TableHead colSpan={principles.length} className="text-center text-xs">
              Frequency (Annually / Half yearly / Quarterly / Any other – please specify)
            </TableHead>
          </TableRow>
          <TableRow className="bg-stone-50">
            {principles.map((principle) => <TableHead key={`review-${principle}`} className="border-r text-center text-xs">{principle}</TableHead>)}
            {principles.map((principle) => <TableHead key={`frequency-${principle}`} className="border-r text-center text-xs">{principle}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell className="sticky left-0 z-10 border-r bg-white text-xs font-medium">{row.label}</TableCell>
              {principles.map((principle) => (
                <TableCell key={`${row.key}-review-${principle}`} className="border-r p-1 text-center">
                  {renderCell(row, 'review_by', principle, reviewOptions)}
                </TableCell>
              ))}
              {principles.map((principle) => (
                <TableCell key={`${row.key}-frequency-${principle}`} className="border-r p-1 text-center">
                  {renderCell(row, 'frequency', principle, frequencyOptions)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function FYComparisonTableRenderer({ config, value, onChange, isEditing, allResponses, lockedPaths = [], dynamicRows = [] }) {
  const tableConfig = config.table_config || {};
  const fixedRows = tableConfig.fixed_rows || [];
  const columns = tableConfig.columns || [
    { key: 'current_fy', label: 'Current FY', type: 'number' },
    { key: 'previous_fy', label: 'Previous FY', type: 'number' }
  ];
  // Normalize FY-suffixed data from backend to simple keys for rendering
  const normalizedValue = normalizeFYResponse(value);
  const data = normalizedValue || {};
  const fyLabels = getFYLabels(allResponses);
  const locked = new Set(lockedPaths);
  const displayRows = (() => {
    if (dynamicRows.length === 0) return fixedRows;
    const childRows = dynamicRows.filter((row) => row.parent_key);
    const standaloneRows = dynamicRows.filter((row) => !row.parent_key);
    const baseRows = fixedRows.filter((row) => !(row.key === 'others' && standaloneRows.length > 0));
    const positionedRows = baseRows.flatMap((row) => [
      row,
      ...childRows.filter((child) => child.parent_key === row.key),
    ]);
    return [...positionedRows, ...standaloneRows];
  })();

  const getColumnLabel = (col) => {
    if (col.key === 'current_fy' || col.label?.toLowerCase().includes('current')) return fyLabels.current;
    if (col.key === 'previous_fy' || col.label?.toLowerCase().includes('previous')) return fyLabels.previous;
    return col.label;
  };

  const handleCellChange = (rowKey, colKey, val) => {
    const newData = { ...data };
    if (!newData[rowKey]) newData[rowKey] = {};
    newData[rowKey][colKey] = val;
    onChange(newData);
  };

  if (fixedRows.length === 0) {
    return (
      <div className="mt-2 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              {columns.map(col => (
                <TableHead key={col.key} className="text-xs font-medium">{getColumnLabel(col)}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.key} className="p-1">
                  {!isEditing ? (
                    <span className="text-sm">{data[col.key] ?? '-'}</span>
                  ) : (
                    <Input type="number" value={data[col.key] ?? ''} onChange={(e) => onChange({ ...data, [col.key]: e.target.value })} className="h-8 text-sm" />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="mt-2 overflow-x-auto">
      <Table className="table-fixed [&_th]:whitespace-normal [&_th]:break-words [&_td]:break-words">
        <TableHeader>
          {tableConfig.column_groups ? (
            (() => {
              // Check if any group has sub_groups (3-level headers)
              const has3Levels = tableConfig.column_groups.some(g => g.sub_groups);
              const totalCols = has3Levels
                ? tableConfig.column_groups.reduce((sum, g) => sum + (g.sub_groups || []).reduce((s2, sg) => s2 + (sg.columns || []).length, 0), 0)
                : tableConfig.column_groups.reduce((sum, g) => sum + (g.columns || []).length, 0);

              if (has3Levels) {
                // Helper to replace FY labels
                const fyLabel = (label) => {
                  if (label.includes('Current')) return fyLabels.current;
                  if (label.includes('Previous')) return fyLabels.previous;
                  return label;
                };
                // Check if Level 3 has any non-empty labels
                const allLevel3Labels = tableConfig.column_groups.flatMap(g =>
                  (g.sub_groups || []).flatMap(sg => (sg.columns || []).map(c => c.label))
                );
                const showLevel3 = allLevel3Labels.some(l => l && l.trim());
                const rowSpan = showLevel3 ? 3 : 2;

                return (
                  <>
                    {/* Level 1: Top group headers */}
                    <TableRow className="bg-stone-100">
                      <TableHead className="text-xs font-semibold w-48 border-b border-r" rowSpan={rowSpan}>Category</TableHead>
                      {tableConfig.column_groups.map((group, gi) => {
                        const colSpan = (group.sub_groups || []).reduce((s, sg) => s + (sg.columns || []).length, 0);
                        return (
                          <TableHead key={gi} colSpan={colSpan} className="text-xs font-semibold text-center border-b border-r last:border-r-0">
                            {fyLabel(group.label)}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                    {/* Level 2: Sub-group headers */}
                    <TableRow className="bg-stone-50/80">
                      {tableConfig.column_groups.flatMap((group, gi) =>
                        (group.sub_groups || []).map((sg, si) => (
                          <TableHead key={`${gi}-${si}`} colSpan={(sg.columns || []).length} className="text-[11px] font-semibold text-center border-b border-r last:border-r-0">
                            {fyLabel(sg.label)}
                          </TableHead>
                        ))
                      )}
                    </TableRow>
                    {/* Level 3: Individual column headers (only if non-empty labels exist) */}
                    {showLevel3 && (
                      <TableRow className="bg-stone-50">
                        {tableConfig.column_groups.flatMap((group) =>
                          (group.sub_groups || []).flatMap((sg) =>
                            (sg.columns || []).map(col => (
                              <TableHead key={col.key} className="text-[10px] font-medium">{col.label}</TableHead>
                            ))
                          )
                        )}
                      </TableRow>
                    )}
                  </>
                );
              }
              // 2-level headers (existing)
              return (
                <>
                  <TableRow className="bg-stone-100">
                    <TableHead className="text-xs font-semibold w-48 border-b border-r" rowSpan={2}>Category</TableHead>
                    {tableConfig.column_groups.map((group, gi) => (
                      <TableHead key={gi} colSpan={group.columns.length} className="text-xs font-semibold text-center border-b border-r last:border-r-0">
                        {group.label.includes('Current') ? fyLabels.current : group.label.includes('Previous') ? fyLabels.previous : group.label}
                      </TableHead>
                    ))}
                  </TableRow>
                  <TableRow className="bg-stone-50">
                    {tableConfig.column_groups.flatMap((group) =>
                      group.columns.map(col => (
                        <TableHead key={col.key} className="text-[11px] font-medium">{col.label}</TableHead>
                      ))
                    )}
                  </TableRow>
                </>
              );
            })()
          ) : (
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs font-medium w-48">Category</TableHead>
              {columns.map(col => (
                <TableHead key={col.key} className="text-xs font-medium">{getColumnLabel(col)}</TableHead>
              ))}
            </TableRow>
          )}
        </TableHeader>
        <TableBody>
          {displayRows.map((row) => (
            <TableRow key={row.key} data-testid={dynamicRows.some(dynamicRow => dynamicRow.key === row.key) ? `kpi-autofill-row-${config.question_key}-${row.key}` : undefined}>
              <TableCell className={`text-xs font-medium ${row.indent ? 'pl-8 text-slate-600 before:mr-2 before:content-["↳"]' : ''}`}>{row.label}</TableCell>
              {columns.map(col => (
                <TableCell key={col.key} className="p-1">
                  {!isEditing || locked.has(`${row.key}.${col.key}`) ? (
                    <span className="text-sm" data-testid={locked.has(`${row.key}.${col.key}`) ? `kpi-autofill-${config.question_key}-${row.key}-${col.key}` : undefined}>{data[row.key]?.[col.key] ?? '-'}</span>
                  ) : (
                    <Input type={col.type === 'number' ? 'number' : 'text'} value={data[row.key]?.[col.key] ?? ''} onChange={(e) => handleCellChange(row.key, col.key, e.target.value)} className="h-8 text-sm" />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function FixedRowTableRenderer({ config, value, onChange, isEditing }) {
  const tableConfig = config.table_config || {};
  const fixedRows = tableConfig.fixed_rows || [];
  const columns = tableConfig.columns || [];
  // Normalize FY-suffixed data from backend to simple keys for rendering
  const normalizedValue = normalizeFYResponse(value);
  const data = normalizedValue || {};

  const handleCellChange = (rowKey, colKey, val) => {
    const newData = { ...data };
    if (!newData[rowKey]) newData[rowKey] = {};
    newData[rowKey][colKey] = val;
    onChange(newData);
  };

  return (
    <div className="mt-2 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-stone-50">
            <TableHead className="text-xs font-medium w-48">Category</TableHead>
            {columns.map(col => (
              <TableHead key={col.key} className="text-xs font-medium">{col.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {fixedRows.map((row) => (
            <TableRow key={row.key}>
              <TableCell className="text-xs font-medium">{row.label}</TableCell>
              {columns.map(col => (
                <TableCell key={col.key} className="p-1">
                  {!isEditing ? (
                    <span className="text-sm">{data[row.key]?.[col.key] ?? '-'}</span>
                  ) : col.type === 'select' ? (
                    <Select value={data[row.key]?.[col.key] || ''} onValueChange={(v) => handleCellChange(row.key, col.key, v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {(col.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={col.type === 'number' ? 'number' : 'text'} value={data[row.key]?.[col.key] ?? ''} onChange={(e) => handleCellChange(row.key, col.key, e.target.value)} className="h-8 text-sm" />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function GroupedMatrixTableRenderer({ config, value, onChange, isEditing, allResponses }) {
  const tableConfig = config.table_config || {};
  const groups = tableConfig.groups || [];
  const columns = tableConfig.columns || [
    { key: 'current_fy', label: 'Current FY' },
    { key: 'previous_fy', label: 'Previous FY' }
  ];
  // Normalize FY-suffixed data from backend to simple keys for rendering
  const normalizedValue = normalizeFYResponse(value);
  const data = normalizedValue || {};
  const fyLabels = getFYLabels(allResponses);

  const getColumnLabel = (col) => {
    if (col.key === 'current_fy' || col.label?.toLowerCase().includes('current')) return fyLabels.current;
    if (col.key === 'previous_fy' || col.label?.toLowerCase().includes('previous')) return fyLabels.previous;
    return col.label;
  };

  const handleCellChange = (groupKey, rowKey, colKey, val) => {
    const newData = { ...data };
    if (!newData[groupKey]) newData[groupKey] = {};
    if (!newData[groupKey][rowKey]) newData[groupKey][rowKey] = {};
    newData[groupKey][rowKey][colKey] = val;
    onChange(newData);
  };

  return (
    <div className="mt-2 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-stone-50">
            <TableHead className="text-xs font-medium w-64">Parameter</TableHead>
            <TableHead className="text-xs font-medium w-80">Metrics</TableHead>
            {columns.map(col => (
              <TableHead key={col.key} className="text-xs font-medium">{getColumnLabel(col)}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(group => (
            group.rows.map((row, rowIdx) => (
              <TableRow key={`${group.key}-${row.key}`} className={rowIdx === 0 ? 'border-t-2' : ''}>
                {rowIdx === 0 && (
                  <TableCell rowSpan={group.rows.length} className="font-medium text-sm align-top bg-stone-50/50">
                    {group.label}
                  </TableCell>
                )}
                <TableCell className="text-sm">{row.label}</TableCell>
                {columns.map(col => (
                  <TableCell key={col.key} className="p-1">
                    {!isEditing ? (
                      <span className="text-sm">{data[group.key]?.[row.key]?.[col.key] ?? '-'}</span>
                    ) : (
                      <Input type="text" value={data[group.key]?.[row.key]?.[col.key] ?? ''} onChange={(e) => handleCellChange(group.key, row.key, col.key, e.target.value)} className="h-8 text-sm" />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
